// src/tattoo/components/AuthButton.jsx
//
// User dropdown menu in the top-left of the editor. The closest thing this
// app has to a "main nav" — Billing, Inbox, and Sign out all hang off this.
//
// Phase 5 of the Leads Inbox feature added:
//   - An "Inbox" entry inside the dropdown, gated on isEntitled("leads").
//   - An unread count badge next to that entry inside the menu.
//   - A small red dot on the collapsed button (visible without opening the
//     menu) so artists see "you have unreads" while in the editor.
//
// useLeadsInbox is called unconditionally because hooks must be. The hook
// is cheap when there's no shop or no entitlement (RLS returns empty rows
// instantly and the realtime subscription only fires once shopId resolves).

import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../../auth/AuthProvider";
import { getProjectJSON, markSaved } from "../state";
import { useSubscription } from "../hooks/useSubscription";
import { useLeadsInbox } from "../hooks/useLeadsInbox";

export default function AuthButton() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // --- Phase 5 inbox bits ---------------------------------------------------
  // Hooks must be called unconditionally; we still gate the menu *render* on
  // entitlement below. For non-entitled / non-artist users, useLeadsInbox
  // returns shopId=null and unreadDirect/unreadPool=0 with no realtime
  // subscription, so this is cheap.
  const sub = useSubscription();
  const inbox = useLeadsInbox();
  const inboxEntitled = !sub.loading && sub.isEntitled("leads");
  const unreadTotal = inbox.unreadDirect + inbox.unreadPool;
  const showUnreadDot = inboxEntitled && unreadTotal > 0;

  useEffect(() => {
    const onDoc = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDoc);
    return () => document.removeEventListener("pointerdown", onDoc);
  }, []);

  if (!session) {
    return (
      <button
        className="btn"
        onClick={() => navigate("/")}
        title="Sign in"
        style={{ padding: "8px 12px" }}
      >
        Sign in
      </button>
    );
  }

  const email = session.user?.email || "Signed in";

  const onSignOut = async () => {
    // 1) Flush current project into the autosave slot before logging out
    try {
      const json = getProjectJSON?.();
      if (json && typeof json === "string") {
        // key MUST match AUTOSAVE_KEY in autosave.js
        localStorage.setItem("tt_autosave_project", json);
        try {
          markSaved?.("signout-flush");
        } catch {}
        try {
          console.log(
            "[auth] flushed autosave on signout",
            (json.length || 0).toLocaleString(),
            "bytes"
          );
        } catch {}
      }
    } catch (e) {
      console.warn("[auth] failed to flush autosave before signout:", e);
    }

    // 2) Normal Supabase sign out
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.warn("[auth] signOut failed:", e);
    } finally {
      setOpen(false);
      navigate("/", { replace: true });
    }
  };

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <button
        className="btn"
        onClick={() => setOpen((v) => !v)}
        style={{ padding: "8px 12px", position: "relative" }}
        aria-expanded={open ? "true" : "false"}
        aria-haspopup="menu"
        title={
          showUnreadDot
            ? `${email} — ${unreadTotal} unread inbox ${
                unreadTotal === 1 ? "item" : "items"
              }`
            : email
        }
      >
        {email}
        <span style={{ marginLeft: 8 }}>▾</span>
        {showUnreadDot && (
          <span
            aria-label={`${unreadTotal} unread inbox items`}
            style={{
              // Small red dot in the top-right corner of the button.
              position: "absolute",
              top: 4,
              right: 4,
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#a2332b",
              boxShadow: "0 0 0 2px #1b1b1b",
              pointerEvents: "none",
            }}
          />
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="menu"
          style={{
            position: "absolute",
            right: 0,
            marginTop: 6,
            minWidth: 240,
            background: "#1b1b1b",
            border: "1px solid #2b2b2b",
            borderRadius: 10,
            padding: 8,
            boxShadow: "0 8px 28px rgba(0,0,0,0.35)",
            zIndex: 20,
          }}
        >
          <div style={{ padding: "6px 10px", fontSize: 12, opacity: 0.8 }}>
            {email}
          </div>
          <hr
            style={{
              border: 0,
              borderTop: "1px solid #2b2b2b",
              margin: "6px 0",
            }}
          />

          {/* Combined studio-images page (logo + studio gallery + flash).
              Shown always when signed in — the page itself surfaces a
              "Set up your studio first" message for users who haven't
              run the wizard yet. Sitting above Inbox because artists
              hit this most regularly (adding flash, refreshing
              gallery shots). Renamed from "Studio profile" to "Studio
              Images" — actual profile fields (address, styles, etc.)
              live in the artist wizard via "Edit studio profile". */}
          <button
            className="btn"
            onClick={() => {
              setOpen(false);
              navigate("/account/studio");
            }}
            style={{
              width: "100%",
              marginBottom: inboxEntitled ? 4 : 0,
            }}
          >
            Studio Images
          </button>

          {inboxEntitled && (
            <button
              className="btn"
              onClick={() => {
                setOpen(false);
                navigate("/inbox");
              }}
              style={{
                width: "100%",
                position: "relative",
              }}
            >
              Inbox
              {unreadTotal > 0 && (
                <span
                  aria-label={`${unreadTotal} unread`}
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: 10,
                    transform: "translateY(-50%)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 22,
                    height: 20,
                    padding: "0 6px",
                    borderRadius: 999,
                    background: "#a2332b",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {unreadTotal > 99 ? "99+" : unreadTotal}
                </span>
              )}
            </button>
          )}

          <button
            className="btn"
            onClick={() => {
              setOpen(false);
              navigate("/account/billing");
            }}
            style={{
              width: "100%",
              textAlign: "left",
              marginTop: inboxEntitled ? 4 : 0,
            }}
          >
            Billing & subscription
          </button>
          <button
            className="btn"
            onClick={onSignOut}
            style={{ width: "100%", textAlign: "left", marginTop: 4 }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
